import { useState } from 'react'
import { useGroupMappings, useCreateGroupMapping, useTestAsUser, useEffectiveAccess } from '@/api/queries'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'

const ROLES = ['Viewer', 'Auditor', 'Persona Author', 'Persona Approver', 'Administrator'] as const

export function AccessRoles() {
  const q = useGroupMappings()
  const create = useCreateGroupMapping()
  const testAs = useTestAsUser()
  const me = useEffectiveAccess()
  const [tab, setTab] = useState<'groups' | 'test' | 'matrix'>('groups')
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({ groupOid: '', role: 'Viewer', scope: '', justification: '' })
  const [testUpn, setTestUpn] = useState('')

  const canManage = (me.data?.capabilities ?? []).includes('access.manage')
  const items = q.data?.items ?? []

  const submit = () => {
    if (!form.groupOid.trim() || !form.justification.trim()) return
    create.mutate({
      groupOid: form.groupOid.trim(),
      role: form.role,
      scope: form.scope ? form.scope.split(',').map((s) => s.trim()) : null,
      justification: form.justification.trim(),
    }, { onSuccess: () => { setOpen(false); setForm({ groupOid: '', role: 'Viewer', scope: '', justification: '' }) } })
  }

  return (
    <div className="page">
      <div className="page-head">
        <div><h1>Access &amp; roles</h1><p>Entra group → app role mapping</p></div>
        <Button variant="primary" disabled={!canManage} onClick={() => setOpen(true)} title={!canManage ? 'Administrator only' : ''}>+ Map a group</Button>
      </div>
      <div className="row" style={{ gap: 8, marginBottom: 'var(--sp-16)' }}>
        <button className={`btn ${tab === 'groups' ? 'primary' : ''}`} onClick={() => setTab('groups')}>Group mappings</button>
        <button className={`btn ${tab === 'test' ? 'primary' : ''}`} onClick={() => setTab('test')}>Test as user</button>
        <button className={`btn ${tab === 'matrix' ? 'primary' : ''}`} onClick={() => setTab('matrix')}>Capability matrix</button>
      </div>

      {tab === 'groups' && (
        <table className="tbl">
          <thead><tr><th>Group</th><th>Role</th><th>Scope</th><th>Created by</th><th>Justification</th></tr></thead>
          <tbody>
            {items.map((g) => (
              <tr key={g.id}>
                <td className="mono">{g.groupDisplayName} <span className="muted" style={{ fontSize: 'var(--fs-100)' }}>({g.groupOid.slice(0, 8)}…)</span></td>
                <td><Badge tone="info">{g.role}</Badge></td>
                <td className="muted">{g.scope ? g.scope.join(', ') : 'all personas'}</td>
                <td className="mono" style={{ fontSize: 'var(--fs-200)' }}>{g.createdBy.slice(0, 12)}</td>
                <td className="muted" style={{ fontSize: 'var(--fs-200)' }}>{g.justification}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5}><div className="empty">No mappings yet.</div></td></tr>}
          </tbody>
        </table>
      )}

      {tab === 'test' && (
        <div className="card pad">
          <p>Preview what role + capabilities a UPN would resolve to (no impersonation).</p>
          <div className="row" style={{ gap: 8 }}>
            <input className="input" value={testUpn} onChange={(e) => setTestUpn(e.target.value)} placeholder="alice@spiinc.net" style={{ flex: 1 }} />
            <Button variant="primary" onClick={() => testAs.mutate(testUpn)} disabled={!testUpn.trim() || testAs.isPending}>Test</Button>
          </div>
          {testAs.data && (
            <div className="stack-12" style={{ marginTop: 'var(--sp-16)' }}>
              <div><b>Effective role:</b> <Badge tone="info">{testAs.data.effectiveRole}</Badge></div>
              <div><b>ACL scope:</b> {testAs.data.aclScope ? testAs.data.aclScope.join(', ') : 'all personas'}</div>
              <div><b>Capabilities:</b> {testAs.data.capabilities.map((c) => <Badge key={c} tone="neutral">{c}</Badge>)}</div>
            </div>
          )}
        </div>
      )}

      {tab === 'matrix' && (
        <table className="matrix">
          <thead><tr><th>Capability ↓ / Role →</th>{ROLES.map((r) => <th key={r}>{r.replace('Persona ', '')}</th>)}</tr></thead>
          <tbody>
            {CAPS.map(([label, cap]) => (
              <tr key={cap}>
                <th style={{ textAlign: 'left' }}>{label}</th>
                {ROLES.map((r) => <td key={r} style={{ textAlign: 'center', color: hasCap(r, cap) ? 'var(--ok)' : 'var(--fg-3)' }}>{hasCap(r, cap) ? '✓' : '·'}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {open && (
        <Modal title="Map an Entra group" onClose={() => setOpen(false)} actions={
          <>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="primary" busy={create.isPending} disabled={!form.groupOid.trim() || !form.justification.trim()} onClick={submit}>Create mapping</Button>
          </>
        }>
          <div className="field">
            <label>Group oid <span className="req">*</span></label>
            <input className="input mono" value={form.groupOid} onChange={(e) => setForm({ ...form, groupOid: e.target.value })} placeholder="00000000-0000-0000-0000-000000000000" />
          </div>
          <div className="field">
            <label>App role</label>
            <select className="input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              {ROLES.map((r) => <option key={r}>{r}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Scope <small>· comma-separated role names, blank = unscoped</small></label>
            <input className="input" value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} placeholder="support, ba" />
          </div>
          <div className="field">
            <label>Justification <span className="req">*</span></label>
            <textarea className="input" value={form.justification} onChange={(e) => setForm({ ...form, justification: e.target.value })} placeholder="Why is this access being granted? (audited)" style={{ minHeight: 70 }} />
          </div>
        </Modal>
      )}
    </div>
  )
}

const CAPS: Array<[string, string]> = [
  ['View personas', 'persona.view'],
  ['Edit body', 'persona.editBody'],
  ['Edit governance fields', 'persona.editGovernance'],
  ['Publish + rollback', 'persona.publish'],
  ['View audit log', 'audit.view'],
  ['Map groups', 'access.manage'],
  ['Reveal secrets', 'secret.reveal'],
]

const ROLE_CAPS: Record<string, Set<string>> = {
  'Viewer':           new Set(['persona.view']),
  'Auditor':          new Set(['persona.view', 'audit.view']),
  'Persona Author':   new Set(['persona.view', 'persona.editBody']),
  'Persona Approver': new Set(['persona.view', 'persona.editBody', 'persona.editGovernance', 'persona.publish']),
  'Administrator':    new Set(['persona.view', 'persona.editBody', 'persona.editGovernance', 'persona.publish', 'audit.view', 'access.manage', 'secret.reveal']),
}

const hasCap = (role: string, cap: string) => ROLE_CAPS[role]?.has(cap) ?? false
